import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { platformApi } from '../../../api/endpoints/platform';
import { handleApiError } from '../../../api/errorMapping';
import { ApiError } from '../../../api/errors';
import { generateIdempotencyKey } from '../../../lib/idempotency';
import { Button, Input } from '@fiado/react-kit';
import {
    RoleScopePicker,
    buildAssigns,
    emptyRoleScope,
    type RoleScopeValue,
} from './RoleScopePicker';
import type {
    PlatformUserDetailLocal,
    CreatePlatformUserResponseLocal,
} from '../../../api/types/PlatformLocal';

const schema = z.object({
    email: z.string().trim().email('Email inválido.'),
    displayName: z.string().trim().min(1, 'Escribe un nombre.'),
});

type FormValues = z.infer<typeof schema>;

interface Props {
    /** Si viene, el form EDITA ese admin (email/pool fijos); si no, CREA uno nuevo. */
    admin?: PlatformUserDetailLocal;
    /** Se llama tras crear/editar OK (ej. cerrar el modal del padre). */
    onDone?: () => void;
    /**
     * Solo en alta: tras crear, si el backend devolvió una contraseña temporal, el padre la muestra en
     * el TempPasswordModal. Recibe la respuesta completa (cognitoSub + email + temporaryPassword?).
     */
    onCreated?: (res: CreatePlatformUserResponseLocal) => void;
}

/**
 * Alta/edición de un ADMIN PLATFORM (`/platform/users`).
 *
 * ALTA: crea el usuario (`POST /platform/users`) y, OPCIONALMENTE, le asigna rol + scope EN EL MISMO form
 * (DEC: UX — antes era un paso aparte vía AssignRoleDialog). El rol es OPCIONAL: si no se elige, se crea sin
 * rol como antes. La asignación reusa el mismo `RoleScopePicker` que el AssignRoleDialog (un roleId + scope
 * global/N-tenants → uno o N assigns `{ roleId, scopeRef }`). DEC-088: el backend resuelve el pool Cognito
 * de la config del tenant 'platform' — el front NO manda userPoolId/region. Reusa idempotencia + el mapeo
 * de error de colisión de email.
 *
 * ERROR PARCIAL (crítico): el alta y las asignaciones son llamadas SEPARADAS. Si el usuario se CREA pero una
 * asignación FALLA, NO se pierde el usuario — se avisa al operador que lo asigne desde la tabla, y se
 * invalida la query igual (el usuario ya existe). Por eso `createPlatformAdmin` y los `assignAdminRole` NO
 * van juntos en una sola promesa que tire todo abajo: el error de asignación se captura aparte.
 *
 * EDICIÓN: solo toca `displayName` (`PUT /platform/users/{sub}`). Sin rol (no se edita rol aquí).
 */
export function PlatformAdminForm({ admin, onDone, onCreated }: Props) {
    const isEdit = !!admin;
    const queryClient = useQueryClient();
    const [roleScope, setRoleScope] = useState<RoleScopeValue>(emptyRoleScope);

    const form = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: {
            email: admin?.email ?? '',
            displayName: admin?.displayName ?? '',
        },
    });

    const invalidate = (): Promise<void> =>
        queryClient.invalidateQueries({ queryKey: ['platform', 'admins'] });

    const mut = useMutation({
        mutationFn: async (v: FormValues) => {
            if (isEdit) {
                await platformApi.updatePlatformAdmin(admin.cognitoSub, {
                    displayName: v.displayName.trim(),
                });
                return null;
            }

            // 1) Crear el usuario. Si esto falla, no hay nada que limpiar (onError mapea el error).
            const created = await platformApi.createPlatformAdmin(
                {
                    email: v.email.trim(),
                    displayName: v.displayName.trim(),
                },
                { idempotencyKey: generateIdempotencyKey() },
            );

            // 2) Si eligió rol, asignarlo (uno o N assigns). El usuario YA está creado: un fallo aquí NO debe
            //    perderlo, así que se captura aparte y se marca `roleAssignFailed` en vez de propagar.
            const assigns = buildAssigns(roleScope);
            let roleAssignFailed = false;
            if (assigns.length > 0) {
                try {
                    for (const body of assigns) {
                        await platformApi.assignAdminRole(created.cognitoSub, body, {
                            idempotencyKey: generateIdempotencyKey(),
                        });
                    }
                } catch {
                    roleAssignFailed = true;
                }
            }

            return {
                created,
                roleAssigned: assigns.length > 0 && !roleAssignFailed,
                roleAssignFailed,
            };
        },
        onSuccess: async (result) => {
            if (isEdit || !result) {
                toast.success('Usuario actualizado');
                await invalidate();
                if (onDone) onDone();
                return;
            }

            const { created, roleAssigned, roleAssignFailed } = result;
            if (roleAssignFailed) {
                // El usuario existe; solo falló la asignación. No se pierde — se avisa cómo recuperarlo.
                toast.warning(
                    'Usuario creado, pero no se pudo asignar el rol. Asígnalo desde la tabla (Gestionar roles).',
                );
            } else if (roleAssigned) {
                toast.success('Usuario creado con su rol asignado');
            } else {
                toast.success('Usuario creado');
            }

            // Siempre invalidar: el usuario fue creado en todos los caminos de éxito del alta.
            await invalidate();
            if (onCreated) onCreated(created);
            if (onDone) onDone();
        },
        onError: (err) => {
            if (err instanceof ApiError && err.code === 'USER_ALREADY_EXISTS') {
                form.setError('email', { message: 'Ya existe un usuario con ese email.' });
                return;
            }
            handleApiError(err);
        },
    });

    return (
        <form
            onSubmit={(e) => {
                void form.handleSubmit((v) => {
                    mut.mutate(v);
                })(e);
            }}
            className="space-y-4"
        >
            <Input
                id="paf-email"
                label="Email"
                type="email"
                disabled={isEdit}
                error={form.formState.errors.email?.message}
                {...form.register('email')}
            />
            <Input
                id="paf-name"
                label="Nombre"
                error={form.formState.errors.displayName?.message}
                {...form.register('displayName')}
            />

            {/* Rol + scope OPCIONAL al crear (sin rol = se crea sin asignación, como antes). En edición no se
                toca el rol — la edición solo cambia displayName. */}
            {!isEdit && (
                <div className="border-t pt-4">
                    <p className="mb-3 text-sm text-muted-foreground">
                        Opcional: asígnale un rol al crear. También puedes hacerlo después desde la
                        tabla.
                    </p>
                    <RoleScopePicker
                        value={roleScope}
                        onChange={setRoleScope}
                        emptyOptionLabel="Sin rol"
                        idPrefix="paf"
                    />
                </div>
            )}

            <Button type="submit" isLoading={mut.isPending}>
                {isEdit ? 'Guardar' : 'Crear'}
            </Button>
        </form>
    );
}
